import { useState } from 'react'
import {
  Card,
  Chip,
  EmptyState,
  LoadingRows,
  inputClass,
  primaryBtn,
  relativeTime,
  secondaryBtn,
} from '../family-finance/shared'
import type { CalendarProvider, IngestChannel, ScanRule } from '../../types/family-planner'
import {
  connectCalendarProvider,
  deleteScanRule,
  disconnectConnection,
  runScan,
  saveScanRule,
  setCalendarEnabled,
  setCalendarMember,
  syncConnection,
} from '../../lib/family-planner-api'
import { isProviderConfigured } from '../../lib/planner-oauth'
import { PROVIDER_META, type PlannerState } from './usePlanner'

const CONNECT_OPTIONS: {
  provider: CalendarProvider & ('google' | 'microsoft')
  label: string
  blurb: string
  setup: string
}[] = [
  {
    provider: 'google',
    label: 'Google Calendar',
    blurb:
      'Sign in on Google’s own consent screen and pick the account. Grants read-only access to your calendars — no password reaches this app, and nothing is written back.',
    setup: 'Set VITE_GOOGLE_CLIENT_ID to an OAuth client ID of type “Web application” with this site as an authorized JavaScript origin.',
  },
  {
    provider: 'microsoft',
    label: 'Microsoft / Outlook',
    blurb:
      'Signs in through the Microsoft identity platform with Calendars.Read. Works with personal and work or school accounts; the link is renewed silently.',
    setup: 'Set VITE_MS_CLIENT_ID to an Azure app registration with this page registered as a Single-page application redirect URI.',
  },
]

export default function SourcesTab({ state }: { state: PlannerState }) {
  const { loading, connections, calendars, rules, refresh } = state
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [draft, setDraft] = useState<{ channel: IngestChannel; label: string; query: string; calendarId: string } | null>(
    null,
  )

  async function guard(key: string, fn: () => Promise<void>) {
    setBusy(key)
    setError(null)
    setNotice(null)
    try {
      await fn()
      refresh()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong')
    } finally {
      setBusy(null)
    }
  }

  async function handleScan(ruleId?: string) {
    await guard(ruleId ? `scan-${ruleId}` : 'scan-all', async () => {
      const found = await runScan(ruleId)
      setNotice(
        found.length === 0
          ? 'Scan finished — nothing new to review.'
          : `Scan finished — ${found.length} new item${found.length === 1 ? '' : 's'} waiting in the Inbox tab.`,
      )
    })
  }

  function startDraft(channel: IngestChannel) {
    setDraft({
      channel,
      label: '',
      query: '',
      calendarId: calendars[0]?.id ?? '',
    })
  }

  async function saveDraft() {
    if (!draft || !draft.label.trim() || !draft.query.trim()) return
    const rule: ScanRule = {
      id: `rule-${draft.channel}-${Date.now()}`,
      channel: draft.channel,
      label: draft.label.trim(),
      query: draft.query.trim(),
      enabled: true,
      lastScan: null,
      matchCount: 0,
      targetCalendarId: draft.calendarId,
      memberIds: [],
    }
    await guard('save-rule', async () => {
      await saveScanRule(rule)
      setDraft(null)
    })
  }

  if (loading) return <LoadingRows rows={5} />

  const gmailRules = rules.filter((r) => r.channel === 'gmail')
  const whatsappRules = rules.filter((r) => r.channel === 'whatsapp')

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}
      {notice && (
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl px-4 py-3 text-blue-600 dark:text-blue-400 text-sm">
          {notice}
        </div>
      )}

      {/* ── Calendar accounts ── */}
      <Card
        title="Calendar accounts"
        subtitle="Every linked account feeds the one aggregated calendar"
        action={
          <button className={secondaryBtn} onClick={() => handleScan()} disabled={busy === 'scan-all'}>
            {busy === 'scan-all' ? 'Scanning…' : 'Scan all sources'}
          </button>
        }
      >
        <div className="space-y-3">
          {connections.map((conn) => {
            const meta = PROVIDER_META[conn.provider]
            const connCalendars = calendars.filter((c) => conn.calendarIds.includes(c.id))
            return (
              <div
                key={conn.id}
                className="border border-slate-200 dark:border-slate-700 rounded-lg p-3 bg-slate-50 dark:bg-slate-900/40"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span>{meta?.icon ?? '📅'}</span>
                      <span className="text-slate-900 dark:text-white text-sm font-medium">{conn.accountEmail}</span>
                      <Chip tone={conn.status === 'connected' ? 'green' : conn.status === 'expired' ? 'amber' : 'red'}>
                        {conn.status}
                      </Chip>
                    </div>
                    <p className="text-slate-500 dark:text-slate-400 text-xs mt-1">
                      {meta?.label} · {conn.scopeSummary}
                    </p>
                    <p className="text-slate-400 dark:text-slate-500 text-xs mt-0.5">
                      Last sync {relativeTime(conn.lastSync)}
                    </p>
                    {conn.error && <p className="text-red-500 text-xs mt-1">{conn.error}</p>}
                  </div>
                  <div className="flex gap-2">
                    <button
                      className={secondaryBtn}
                      onClick={() => guard(`sync-${conn.id}`, async () => void (await syncConnection(conn.id)))}
                      disabled={busy === `sync-${conn.id}`}
                    >
                      {busy === `sync-${conn.id}` ? 'Syncing…' : 'Sync'}
                    </button>
                    <button
                      className={secondaryBtn}
                      onClick={() => guard(`disc-${conn.id}`, () => disconnectConnection(conn.id))}
                      disabled={busy === `disc-${conn.id}`}
                    >
                      Disconnect
                    </button>
                  </div>
                </div>

                <div className="mt-3 space-y-1.5">
                  {connCalendars.map((cal) => (
                    <div key={cal.id} className="flex flex-wrap items-center gap-2 text-sm">
                      <label className="flex items-center gap-2 cursor-pointer min-w-0 flex-1">
                        <input
                          type="checkbox"
                          checked={cal.enabled}
                          onChange={(e) =>
                            guard(`cal-${cal.id}`, () => setCalendarEnabled(cal.id, e.target.checked))
                          }
                          className="rounded border-slate-400"
                        />
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: cal.color }} />
                        <span className="text-slate-700 dark:text-slate-200 truncate">{cal.name}</span>
                        {cal.readOnly && <Chip tone="slate">read-only</Chip>}
                      </label>
                      {/* Attribution drives the per-person filters, conflict
                          detection, and free-window search. */}
                      <select
                        className="bg-slate-100 dark:bg-slate-900 text-slate-700 dark:text-slate-200 text-xs rounded-md px-2 py-1 border border-slate-300 dark:border-slate-600"
                        value={cal.memberId ?? ''}
                        onChange={(e) =>
                          guard(`member-${cal.id}`, () => setCalendarMember(cal.id, e.target.value || null))
                        }
                      >
                        <option value="">Nobody in particular</option>
                        {state.members.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}

          <div className="grid sm:grid-cols-2 gap-3 pt-1">
            {CONNECT_OPTIONS.map((opt) => {
              const linked = connections.some((c) => c.provider === opt.provider)
              const ready = isProviderConfigured(opt.provider)
              return (
                <div
                  key={opt.provider}
                  className="border border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-3"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span>{PROVIDER_META[opt.provider].icon}</span>
                    <span className="text-slate-900 dark:text-white text-sm font-medium">{opt.label}</span>
                    {linked && <Chip tone="green">linked</Chip>}
                    {!ready && <Chip tone="amber">setup needed</Chip>}
                  </div>
                  <p className="text-slate-500 dark:text-slate-400 text-xs mb-3">{opt.blurb}</p>
                  {ready ? (
                    <button
                      className={primaryBtn}
                      onClick={() =>
                        guard(`connect-${opt.provider}`, async () => void (await connectCalendarProvider(opt.provider)))
                      }
                      disabled={busy === `connect-${opt.provider}`}
                    >
                      {busy === `connect-${opt.provider}`
                        ? 'Opening consent…'
                        : linked
                          ? `Add another ${opt.label} account`
                          : `Connect ${opt.label}`}
                    </button>
                  ) : (
                    <p className="text-amber-600 dark:text-amber-400 text-xs">{opt.setup}</p>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </Card>

      {/* ── Gmail invitation scanning ── */}
      <RuleSection
        title="Gmail invitation scanning"
        subtitle="Saved Gmail searches. Matches are parsed into the review inbox — never written straight to a calendar."
        icon="✉️"
        channel="gmail"
        rules={gmailRules}
        state={state}
        busy={busy}
        onScan={handleScan}
        onToggle={(rule) =>
          guard(`toggle-${rule.id}`, async () => void (await saveScanRule({ ...rule, enabled: !rule.enabled })))
        }
        onDelete={(rule) => guard(`del-${rule.id}`, () => deleteScanRule(rule.id))}
        onAdd={() => startDraft('gmail')}
      />

      {/* ── WhatsApp threads ── */}
      <RuleSection
        title="WhatsApp threads"
        subtitle="Watched threads. See the note below on how messages actually reach the planner."
        icon="💬"
        channel="whatsapp"
        rules={whatsappRules}
        state={state}
        busy={busy}
        onScan={handleScan}
        onToggle={(rule) =>
          guard(`toggle-${rule.id}`, async () => void (await saveScanRule({ ...rule, enabled: !rule.enabled })))
        }
        onDelete={(rule) => guard(`del-${rule.id}`, () => deleteScanRule(rule.id))}
        onAdd={() => startDraft('whatsapp')}
      />

      <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 text-sm">
        <p className="text-amber-700 dark:text-amber-400 font-medium">Connecting a WhatsApp group thread</p>
        <p className="text-amber-700/80 dark:text-amber-400/80 text-xs mt-1 leading-relaxed">
          WhatsApp does not offer any API that reads your personal or group chats. The Cloud API only delivers messages
          sent <em>to</em> a number registered to a business account, and the unofficial web-client libraries are a
          terms-of-service violation that gets real numbers banned — so there is no “log in and watch this thread”
          option to build, from this app or any other.
        </p>
        <p className="text-amber-700/80 dark:text-amber-400/80 text-xs mt-2 leading-relaxed">
          What works instead, in order of least effort:
        </p>
        <ul className="text-amber-700/80 dark:text-amber-400/80 text-xs mt-1 space-y-1 list-disc pl-4 leading-relaxed">
          <li>
            <strong>Share the message in.</strong> Install this site as an app, then in the WhatsApp thread long-press a
            message → Share → <strong>NB Info</strong>. It opens here with the text already parsed and waiting in the
            Inbox. On iPhone, a two-step Shortcut does the same thing — see the architecture doc.
          </li>
          <li>
            <strong>Paste it.</strong> The Inbox paste box reads WhatsApp’s exported transcript format
            (<code>[8/17/26, 6:31 PM] Dana: …</code>) and pulls out the sender and the date.
          </li>
          <li>
            <strong>Cloud API number</strong> — the only fully automatic route. If the group runs through a number the
            planner owns, its messages arrive as webhooks. Requires the backend service plus moving the group to that
            number.
          </li>
        </ul>
      </div>

      {draft && (
        <Card title={`New ${draft.channel === 'gmail' ? 'Gmail search' : 'WhatsApp thread'}`}>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-slate-500 dark:text-slate-400 text-xs block mb-1">Label</label>
              <input
                className={inputClass}
                value={draft.label}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder={draft.channel === 'gmail' ? 'Scout troop emails' : 'Neighborhood group'}
              />
            </div>
            <div>
              <label className="text-slate-500 dark:text-slate-400 text-xs block mb-1">
                {draft.channel === 'gmail' ? 'Gmail search query' : 'Thread name'}
              </label>
              <input
                className={inputClass}
                value={draft.query}
                onChange={(e) => setDraft({ ...draft, query: e.target.value })}
                placeholder={draft.channel === 'gmail' ? 'from:(troop.example.org) newer_than:30d' : 'Troop 42 Parents'}
              />
            </div>
            <div>
              <label className="text-slate-500 dark:text-slate-400 text-xs block mb-1">Accepted events go to</label>
              <select
                className={inputClass}
                value={draft.calendarId}
                onChange={(e) => setDraft({ ...draft, calendarId: e.target.value })}
              >
                {calendars
                  .filter((c) => !c.readOnly)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button className={primaryBtn} onClick={saveDraft} disabled={busy === 'save-rule'}>
              Save source
            </button>
            <button className={secondaryBtn} onClick={() => setDraft(null)}>
              Cancel
            </button>
          </div>
        </Card>
      )}
    </div>
  )
}

function RuleSection({
  title,
  subtitle,
  icon,
  rules,
  state,
  busy,
  onScan,
  onToggle,
  onDelete,
  onAdd,
}: {
  title: string
  subtitle: string
  icon: string
  channel: IngestChannel
  rules: ScanRule[]
  state: PlannerState
  busy: string | null
  onScan: (ruleId?: string) => void
  onToggle: (rule: ScanRule) => void
  onDelete: (rule: ScanRule) => void
  onAdd: () => void
}) {
  return (
    <Card
      title={`${icon} ${title}`}
      subtitle={subtitle}
      action={
        <button className={secondaryBtn} onClick={onAdd}>
          + Add
        </button>
      }
    >
      {rules.length === 0 ? (
        <EmptyState icon={icon} message="No sources configured yet." />
      ) : (
        <div className="space-y-2">
          {rules.map((rule) => (
            <div
              key={rule.id}
              className="flex flex-wrap items-center justify-between gap-3 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-slate-900 dark:text-white text-sm font-medium">{rule.label}</span>
                  <Chip tone={rule.enabled ? 'green' : 'slate'}>{rule.enabled ? 'active' : 'paused'}</Chip>
                </div>
                <p className="text-slate-500 dark:text-slate-400 text-xs font-mono mt-0.5 truncate">{rule.query}</p>
                <p className="text-slate-400 dark:text-slate-500 text-xs mt-0.5">
                  {rule.matchCount} matched · last scan {relativeTime(rule.lastScan)} · →{' '}
                  {state.calendarById(rule.targetCalendarId)?.name ?? 'unassigned'}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  className={secondaryBtn}
                  onClick={() => onScan(rule.id)}
                  disabled={busy === `scan-${rule.id}` || !rule.enabled}
                >
                  {busy === `scan-${rule.id}` ? 'Scanning…' : 'Scan now'}
                </button>
                <button className={secondaryBtn} onClick={() => onToggle(rule)}>
                  {rule.enabled ? 'Pause' : 'Resume'}
                </button>
                <button className={secondaryBtn} onClick={() => onDelete(rule)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  )
}
